import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, FolderOpen, ListChecks, Plus, Star, Users } from 'lucide-react';
import { projectsAPI, templatesAPI } from '../lib/api';
import { getUser } from '../lib/auth';
import { t } from '../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, cardCls, inputCls } from '../components/ui';

function ProjectTile({ project, onFavorite }) {
  const pct = project.task_count ? Math.round((project.done_count / project.task_count) * 100) : 0;
  const fav = (project.favorite_member_ids || []).length > 0;
  const memberCount = (project.member_ids || []).length + (project.guest_ids || []).length;
  return (
    <div className={`group relative overflow-hidden ${cardCls}`}>
      <span className="absolute inset-x-0 top-0 h-0.5" style={{ background: project.color }} aria-hidden />
      <Link to={`/projects/${project.id}`} className="block p-4">
        <div className="flex items-start gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ring-1 ring-inset ring-white/5"
            style={{ background: `${project.color}22`, color: project.color }}
          >
            {(project.icon || project.name[0] || '?').slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 pr-5">
            <div className="truncate text-sm font-semibold">{project.name}</div>
            <div className="mt-0.5 truncate text-xs text-pm-faint">
              {project.description
                ? project.description
                : `${project.done_count}/${project.task_count} ${t('tasks')}`}
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2.5">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-pm-raised">
            <div
              className="h-full rounded-full transition-[width] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ width: `${pct}%`, background: project.color }}
            />
          </div>
          <span className="pm-num w-8 shrink-0 text-right text-[11px] font-medium text-pm-faint">{pct}%</span>
        </div>
        <div className="mt-3 flex items-center gap-3.5 border-t border-pm-border/60 pt-2.5 text-xs text-pm-faint">
          <span className="inline-flex items-center gap-1" title={t('tasks')}>
            <ListChecks size={13} aria-hidden />
            <span className="pm-num">{project.done_count}/{project.task_count}</span>
          </span>
          {memberCount > 0 && (
            <span className="inline-flex items-center gap-1" title="Members">
              <Users size={13} aria-hidden />
              <span className="pm-num">{memberCount}</span>
            </span>
          )}
        </div>
      </Link>
      <button
        type="button"
        onClick={() => onFavorite(project)}
        className={`absolute right-2 top-2.5 rounded p-1 ${
          fav ? 'text-pm-accent' : 'text-pm-faint opacity-0 group-hover:opacity-100'
        } hover:text-pm-accent`}
        title={fav ? 'Unfavorite' : 'Favorite'}
      >
        <Star size={14} fill={fav ? 'currentColor' : 'none'} />
      </button>
    </div>
  );
}

export default function PortfolioHome() {
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const user = getUser();

  const load = useCallback(async () => {
    const { data } = await projectsAPI.list(archived);
    setProjects(data.projects);
    setFolders(data.folders);
  }, [archived]);

  useEffect(() => {
    load();
    templatesAPI.list().then(({ data }) => setTemplates(data.templates));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (templateId) await templatesAPI.instantiate(templateId, name);
      else await projectsAPI.create({ name, description, folder_id: folderId || null });
      setCreating(false);
      setName('');
      setDescription('');
      setFolderId('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const toggleFavorite = async (project) => {
    await projectsAPI.favorite(project.id);
    await load();
  };

  const favorites = projects.filter((p) => (p.favorite_member_ids || []).length > 0);
  const byFolder = folders
    .map((f) => ({ folder: f, items: projects.filter((p) => p.folder_id === f.id) }))
    .filter((g) => g.items.length > 0);
  const loose = projects.filter((p) => !p.folder_id || !folders.some((f) => f.id === p.folder_id));

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('Projects')}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setArchived(!archived)}
            className={`${btnGhost} inline-flex items-center gap-1.5 ${archived ? 'border-pm-accent text-pm-accent' : ''}`}
          >
            <Archive size={14} />
            {archived ? t('Showing archived') : t('Archived')}
          </button>
          {(user?.role === 'admin' || user?.pr === 'operator') ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className={`${btnPrimary} inline-flex items-center gap-1.5`}
            >
              <Plus size={14} />
              {t('New project')}
            </button>
          ) : null}
        </div>
      </div>

      {projects.length === 0 && (
        <div className="rounded-lg border border-dashed border-pm-border py-16 text-center">
          <p className="text-sm text-pm-muted">
            {archived ? t('No archived projects.') : t('No projects yet — create the first one.')}
          </p>
        </div>
      )}

      {favorites.length > 0 && !archived && (
        <section className="mb-8">
          <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-pm-faint">
            <Star size={12} /> {t('Favorites')}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {favorites.map((p) => (
              <ProjectTile key={p.id} project={p} onFavorite={toggleFavorite} />
            ))}
          </div>
        </section>
      )}

      {byFolder.map(({ folder, items }) => (
        <section key={folder.id} className="mb-8">
          <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-pm-faint">
            <FolderOpen size={12} /> {folder.name}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <ProjectTile key={p.id} project={p} onFavorite={toggleFavorite} />
            ))}
          </div>
        </section>
      ))}

      {loose.length > 0 && (
        <section>
          {byFolder.length > 0 && (
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-pm-faint">{t('Other')}</h2>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {loose.map((p) => (
              <ProjectTile key={p.id} project={p} onFavorite={toggleFavorite} />
            ))}
          </div>
        </section>
      )}

      {creating && (
        <Modal title={t('New project')} onClose={() => setCreating(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label={t('Name')}>
              <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className={inputCls} />
            </Field>
            {templates.length > 0 && (
              <Field label={t('Start from')}>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputCls}>
                  <option value="">{t('Blank project')}</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      Template: {t.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label={t('Description (optional)')}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={inputCls}
              />
            </Field>
            {folders.length > 0 && (
              <Field label={t('Folder (optional)')}>
                <select value={folderId} onChange={(e) => setFolderId(e.target.value)} className={inputCls}>
                  <option value="">{t('No folder')}</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className={btnGhost}>
                {t('Cancel')}
              </button>
              <button type="submit" disabled={busy || !name.trim()} className={btnPrimary}>
                {busy ? t('Creating…') : t('Create project')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
